/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
// FLAGGED-FOR-REVIEW-2026-02: This file has residual unused vars / state
// setters / effect-deps that look like DROPPED-FEATURE references, not
// blind-orphan code (state setters never called, computed values never
// rendered, hooks with intentional dep omissions). Reviewer next step:
// walk each warning and decide either RESTORE the callsite that references
// the var, OR delete the var. Do NOT bulk-delete without review — several
// of these signal features that were half-migrated.
import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  Users, Search, Plus, Trash2, Upload, X, Save, Clapperboard, Wand2
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { formatSceneNumber } from '../utils/sceneNumber';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { ImageLightbox } from '../components/ImageLightbox';
import { AuthImage } from '../components/AuthImage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Helpers ──────────────────────────────────────────────────────────────────

// SEC-002 (closed 2026-07-06): `photoUrl` is now a soft-deprecation stub.
// The legacy `?auth=<jwt>` URL is rejected by the backend. Callers should
// render via <AuthImage path={...} /> (uses lazy 60-second dtoken).
function photoUrl(storage_path /*, token unused */) {
  return `${API}/files?path=${encodeURIComponent(storage_path)}`;
}

// ── CharacterCard ─────────────────────────────────────────────────────────────

function CharacterCard({ char, profile, token, onClick }) {
  const photo = profile?.photos?.[0];
  const shootDays = profile?.shoot_days || [];

  return (
    <Card
      onClick={onClick}
      className="bg-[#1a1a1a] border-[#2a2a2a] p-4 hover:border-[#e8c547]/50 transition-all cursor-pointer group"
      data-testid={`character-card-${char.name}`}
    >
      <div className="flex items-start gap-3 mb-3">
        {/* Avatar */}
        <div className="h-12 w-12 rounded-full overflow-hidden shrink-0 bg-gradient-to-br from-[#e8c547] to-amber-700 flex items-center justify-center">
          {photo ? (
            <AuthImage
              path={photo.storage_path}
              alt={char.name}
              className="h-full w-full object-cover"
            />
          ) : (
            <span className="text-black font-bold text-lg">{char.name.charAt(0).toUpperCase()}</span>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="font-semibold text-[#f0f0f0] text-sm truncate">{char.name}</div>
          {profile?.actor_name && (
            <div className="text-xs text-[#a0a0a0] truncate">as {profile.actor_name}</div>
          )}
          <div className="text-xs text-[#666] mt-0.5">
            {char.scenes.length} scene{char.scenes.length !== 1 ? 's' : ''}
          </div>
        </div>

        <Badge className="bg-[#e8c547]/15 text-[#e8c547] border-0 text-xs shrink-0">
          {Math.floor(char.totalPages / 8)}{char.totalPages % 8 > 0 ? ` ${char.totalPages % 8}/8` : ''} pg
        </Badge>
      </div>

      {/* Scene badges */}
      <div className="flex flex-wrap gap-1 mb-2">
        {char.scenes.slice(0, 8).map(s => (
          <Badge key={s.id} variant="outline" className="border-[#2a2a2a] text-[#a0a0a0] text-xs px-1.5 py-0">
            {formatSceneNumber(s.scene_number)}
          </Badge>
        ))}
        {char.scenes.length > 8 && (
          <Badge variant="outline" className="border-[#2a2a2a] text-[#666] text-xs px-1.5 py-0">
            +{char.scenes.length - 8}
          </Badge>
        )}
      </div>

      {/* Shoot day badges */}
      {shootDays.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2 pt-2 border-t border-[#2a2a2a]">
          {shootDays.map(d => (
            <span key={d} className="text-[10px] bg-[#4a9eff]/20 text-[#4a9eff] px-1.5 py-0.5 rounded">
              Day {d}
            </span>
          ))}
        </div>
      )}

      {/* Costumes hint */}
      {(profile?.costumes?.length > 0) && (
        <div className="mt-2 text-xs text-[#666] group-hover:text-[#a0a0a0] transition-colors">
          {profile.costumes.length} costume{profile.costumes.length !== 1 ? 's' : ''}
        </div>
      )}
    </Card>
  );
}


// ── CharacterModal ────────────────────────────────────────────────────────────

function CharacterModal({ char, initialProfile, token, projectId, onClose, onProfileUpdated }) {
  const [profile, setProfile] = useState(initialProfile || {
    photos: [], costumes: [], shoot_days: [], description: '', actor_name: '', notes: ''
  });
  const [actorName, setActorName] = useState(initialProfile?.actor_name || '');
  const [description, setDescription] = useState(initialProfile?.description || '');
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [addingCostume, setAddingCostume] = useState(false);
  const [newCostume, setNewCostume] = useState({ name: '', description: '', scene_numbers: '' });
  const [photoTag, setPhotoTag] = useState('');
  const [editingPhotoId, setEditingPhotoId] = useState(null);
  const photoInputRef = useRef();
  const headers = { Authorization: `Bearer ${token}` };

  const profileBase = `/api/projects/${projectId}/character-profiles/${encodeURIComponent(char.name)}`;

  // ── Compute costume continuity from scene breakdown items ──────────────────
  const costumeContinuity = (() => {
    const map = {}; // costumeDesc → [sceneNumbers]
    (char.scenes || []).forEach(scene => {
      (scene.breakdown_items?.COSTUME || [])
        .filter(c => c.character_name === char.name)
        .forEach(c => {
          const key = c.description || 'Unnamed Costume';
          if (!map[key]) map[key] = [];
          // Skip scenes with no scene_number — they would render as 'null' downstream.
          if (scene.scene_number == null || scene.scene_number === '') return;
          if (!map[key].includes(scene.scene_number)) map[key].push(scene.scene_number);
        });
    });
    return Object.entries(map).map(([desc, scenes], idx) => ({
      idx: idx + 1,
      desc,
      scenes: scenes.sort((a, b) => a - b),
    }));
  })();

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.patch(`${process.env.REACT_APP_BACKEND_URL}${profileBase}`,
        { description, actor_name: actorName }, { headers });
      const updated = { ...profile, ...res.data };
      setProfile(updated);
      onProfileUpdated(char.name, updated);
      toast.success('Profile saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleGenerateDescription = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}${profileBase}/generate-description`,
        {}, { headers }
      );
      setDescription(res.data.description);
      toast.success('Description generated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI generation failed');
    } finally { setGenerating(false); }
  };

  const handlePhotoUpload = async (file, tag = '') => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (tag) fd.append('tag', tag);
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}${profileBase}/photos`, fd,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
      );
      const newPhoto = { ...res.data, tag };
      const updated = { ...profile, photos: [...(profile.photos || []), newPhoto] };
      setProfile(updated);
      onProfileUpdated(char.name, updated);
      toast.success('Photo uploaded');
      setPhotoTag('');
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}${profileBase}/photos/${photoId}`, { headers }
      );
      const updated = { ...profile, photos: profile.photos.filter(p => p.id !== photoId) };
      setProfile(updated);
      onProfileUpdated(char.name, updated);
      toast.success('Photo removed');
    } catch { toast.error('Failed to remove'); }
  };

  const handleAddCostume = async () => {
    if (!newCostume.name.trim()) return;
    const scene_numbers = newCostume.scene_numbers
      ? newCostume.scene_numbers.split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n))
      : [];
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}${profileBase}/costumes`,
        { name: newCostume.name.trim(), description: newCostume.description, scene_numbers },
        { headers }
      );
      const updated = { ...profile, costumes: [...(profile.costumes || []), res.data] };
      setProfile(updated);
      onProfileUpdated(char.name, updated);
      setNewCostume({ name: '', description: '', scene_numbers: '' });
      setAddingCostume(false);
      toast.success('Costume added');
    } catch { toast.error('Failed to add costume'); }
  };

  const handleDeleteCostume = async (costumeId) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}${profileBase}/costumes/${costumeId}`, { headers }
      );
      const updated = { ...profile, costumes: profile.costumes.filter(c => c.id !== costumeId) };
      setProfile(updated);
      onProfileUpdated(char.name, updated);
      toast.success('Costume removed');
    } catch { toast.error('Failed to remove'); }
  };

  const photos = profile.photos || [];
  const shootDays = profile.shoot_days || [];

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent
        className="bg-[#111] border-[#2a2a2a] text-[#f0f0f0] max-w-4xl max-h-[90vh] overflow-y-auto p-0"
        data-testid="cast-expanded-modal"
      >
        <div className="flex flex-col md:flex-row h-full">

          {/* ── Left: Photos ───────────────────────────────── */}
          <div className="md:w-64 shrink-0 bg-[#0d0d0d] p-4 flex flex-col gap-3 border-b md:border-b-0 md:border-r border-[#2a2a2a]">
            {/* Main photo */}
            <div
              className="aspect-square rounded-lg overflow-hidden bg-gradient-to-br from-[#e8c547]/20 to-amber-900/30 flex items-center justify-center cursor-pointer relative group"
              onClick={() => photos.length > 0 && setLightbox({ images: photos.map(p => ({ path: p.storage_path, alt: p.tag || p.original_filename })), idx: 0 })}
            >
              {photos.length > 0 ? (
                <>
                  <AuthImage path={photos[0].storage_path} alt={char.name} className="w-full h-full object-cover" />
                  {photos[0].tag && (
                    <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-xs text-[#e8c547] px-2 py-1 truncate">{photos[0].tag}</div>
                  )}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                    {photos.length > 1 && (
                      <span className="opacity-0 group-hover:opacity-100 text-xs text-white bg-black/60 px-2 py-1 rounded-full transition-opacity">
                        {photos.length} photos
                      </span>
                    )}
                  </div>
                </>
              ) : (
                <span className="text-5xl font-bold text-[#e8c547]/50">{char.name.charAt(0).toUpperCase()}</span>
              )}
            </div>

            {/* Photo thumbnails */}
            {photos.length > 1 && (
              <div className="flex flex-wrap gap-1.5">
                {photos.map((ph, i) => (
                  <div key={ph.id} className="relative group/thumb">
                    <div className="h-14 w-14 rounded overflow-hidden border border-[#2a2a2a]">
                      <AuthImage
                        path={ph.storage_path}
                        alt={ph.tag || ph.original_filename}
                        className="w-full h-full object-cover cursor-pointer"
                        onClick={() => setLightbox({ images: photos.map(p => ({ path: p.storage_path, alt: p.tag || p.original_filename })), idx: i })}
                      />
                    </div>
                    {ph.tag && <div className="text-[8px] text-[#e8c547] truncate mt-0.5 max-w-[56px]">{ph.tag}</div>}
                    <button
                      onClick={() => handleDeletePhoto(ph.id)}
                      className="absolute top-0.5 right-0.5 bg-black/70 rounded-full p-0.5 opacity-0 group-hover/thumb:opacity-100 transition-opacity"
                      data-testid={`delete-photo-${ph.id}`}
                    >
                      <X className="h-2.5 w-2.5 text-white" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Upload with optional tag */}
            <div className="space-y-1.5">
              <Input
                value={photoTag}
                onChange={e => setPhotoTag(e.target.value)}
                placeholder="Photo label (e.g. Act 1 Look)"
                className="bg-[#1a1a1a] border-[#2a2a2a] text-[#f0f0f0] text-xs h-7"
                data-testid="photo-tag-input"
              />
              <input
                ref={photoInputRef} type="file" accept="image/*" className="hidden"
                onChange={e => handlePhotoUpload(e.target.files?.[0], photoTag)}
              />
              <Button
                variant="outline" size="sm"
                className="border-[#2a2a2a] text-[#a0a0a0] hover:text-[#f0f0f0] hover:border-[#e8c547]/40 text-xs w-full"
                onClick={() => photoInputRef.current?.click()}
                disabled={uploading}
                data-testid="upload-cast-photo-btn"
              >
                <Upload className="h-3 w-3 mr-1.5" />
                {uploading ? 'Uploading...' : photos.length > 0 ? 'Add Photo' : 'Upload Photo'}
              </Button>
            </div>
          </div>

          {/* ── Right: Details ─────────────────────────────── */}
          <div className="flex-1 p-5 overflow-y-auto">
            <DialogHeader className="mb-4">
              <DialogTitle className="text-xl font-bold text-[#f0f0f0]">{char.name}</DialogTitle>
              <DialogDescription className="sr-only">Character profile for {char.name}</DialogDescription>
              <p className="text-xs text-[#666]">
                {char.scenes.length} scene{char.scenes.length !== 1 ? 's' : ''} &nbsp;·&nbsp;
                {Math.floor(char.totalPages / 8)}{char.totalPages % 8 > 0 ? ` ${char.totalPages % 8}/8` : ''} pages
              </p>
            </DialogHeader>

            {/* Actor name */}
            <div className="mb-3">
              <label className="text-xs text-[#a0a0a0] mb-1 block">Actor / Cast</label>
              <Input value={actorName} onChange={e => setActorName(e.target.value)} placeholder="Actor name..."
                className="bg-[#1a1a1a] border-[#2a2a2a] text-[#f0f0f0] text-sm h-8" data-testid="cast-actor-name-input" />
            </div>

            {/* Description */}
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-[#a0a0a0]">Character Description</label>
                <Button variant="ghost" size="sm" className="h-6 px-2 text-xs text-[#e8c547]/70 hover:text-[#e8c547]"
                  onClick={handleGenerateDescription} disabled={generating} data-testid="ai-generate-description-btn">
                  <Wand2 className="h-3 w-3 mr-1" />
                  {generating ? 'Generating...' : 'AI Generate'}
                </Button>
              </div>
              <Textarea value={description} onChange={e => setDescription(e.target.value)}
                placeholder="Character description, arc, notes..."
                className="bg-[#1a1a1a] border-[#2a2a2a] text-[#f0f0f0] text-sm resize-none min-h-[70px]"
                rows={3} data-testid="cast-description-input" />
            </div>

            <Button size="sm" className="bg-[#e8c547] hover:bg-[#d4b340] text-black font-medium text-xs mb-5"
              onClick={handleSave} disabled={saving} data-testid="cast-save-profile-btn">
              <Save className="h-3.5 w-3.5 mr-1.5" />{saving ? 'Saving...' : 'Save Profile'}
            </Button>

            {/* Shoot Days */}
            {shootDays.length > 0 && (
              <div className="mb-4">
                <div className="text-xs text-[#a0a0a0] uppercase tracking-widest mb-2">Shoot Days</div>
                <div className="flex flex-wrap gap-1.5">
                  {shootDays.map(d => (
                    <span key={d} className="text-xs bg-[#4a9eff]/20 text-[#4a9eff] border border-[#4a9eff]/30 px-2.5 py-1 rounded-full" data-testid={`shoot-day-badge-${d}`}>Day {d}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Scene appearances */}
            <div className="mb-4">
              <div className="text-xs text-[#a0a0a0] uppercase tracking-widest mb-2">Appears In</div>
              <div className="flex flex-wrap gap-1">
                {char.scenes.map(s => (
                  <span key={s.id} className="text-xs border border-[#2a2a2a] text-[#a0a0a0] px-2 py-0.5 rounded-full">
                    Sc {formatSceneNumber(s.scene_number)}
                  </span>
                ))}
              </div>
            </div>

            {/* ── Costume Continuity ─────────────────────────── */}
            {costumeContinuity.length > 0 && (
              <div className="mb-4">
                <div className="text-xs text-[#a0a0a0] uppercase tracking-widest mb-2">Costume Continuity</div>
                <div className="space-y-2">
                  {costumeContinuity.map(({ idx, desc, scenes }) => (
                    <div key={desc} className="flex items-start gap-3 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg px-3 py-2">
                      <span className="text-[10px] font-bold bg-[#e8c547]/20 text-[#e8c547] px-2 py-0.5 rounded-full shrink-0 mt-0.5">
                        Look {idx}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-[#f0f0f0] font-medium">{desc}</div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {scenes.map(n => (
                            <span key={n} className="text-[10px] bg-[#2a2a2a] text-[#a0a0a0] px-1.5 py-0.5 rounded">Sc {n}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Manually tagged Costumes ───────────────────── */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs text-[#a0a0a0] uppercase tracking-widest">
                  Costumes {(profile.costumes || []).length > 0 && `(${profile.costumes.length})`}
                </div>
                <Button variant="ghost" size="sm" className="h-6 px-2 text-xs text-[#e8c547]/70 hover:text-[#e8c547]"
                  onClick={() => setAddingCostume(v => !v)} data-testid="add-costume-toggle-btn">
                  <Plus className="h-3 w-3 mr-1" /> Add
                </Button>
              </div>

              {addingCostume && (
                <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-3 mb-3 space-y-2">
                  <Input value={newCostume.name} onChange={e => setNewCostume(v => ({ ...v, name: e.target.value }))}
                    placeholder="Costume name (e.g. Wedding Saree)"
                    className="bg-[#111] border-[#333] text-[#f0f0f0] text-sm h-8" data-testid="new-costume-name-input" />
                  <Input value={newCostume.description} onChange={e => setNewCostume(v => ({ ...v, description: e.target.value }))}
                    placeholder="Description (optional)"
                    className="bg-[#111] border-[#333] text-[#f0f0f0] text-sm h-8" data-testid="new-costume-description-input" />
                  <Input value={newCostume.scene_numbers} onChange={e => setNewCostume(v => ({ ...v, scene_numbers: e.target.value }))}
                    placeholder="Scene numbers (e.g. 1, 3, 7)"
                    className="bg-[#111] border-[#333] text-[#f0f0f0] text-sm h-8" data-testid="new-costume-scenes-input" />
                  <div className="flex gap-2">
                    <Button size="sm" className="bg-[#e8c547] hover:bg-[#d4b340] text-black text-xs"
                      onClick={handleAddCostume} data-testid="save-new-costume-btn">Add Costume</Button>
                    <Button size="sm" variant="ghost" className="text-xs text-[#666]"
                      onClick={() => { setAddingCostume(false); setNewCostume({ name: '', description: '', scene_numbers: '' }); }}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {(profile.costumes || []).length > 0 ? (
                <div className="space-y-2">
                  {profile.costumes.map(c => (
                    <div key={c.id} className="flex items-start gap-2 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-2.5 group" data-testid={`costume-item-${c.id}`}>
                      <Clapperboard className="h-3.5 w-3.5 text-[#e8c547] mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-[#f0f0f0]">{c.name}</div>
                        {c.description && <div className="text-xs text-[#a0a0a0] mt-0.5">{c.description}</div>}
                        {c.scene_numbers?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {c.scene_numbers.map(n => (
                              <span key={n} className="text-[10px] bg-[#2a2a2a] text-[#a0a0a0] px-1.5 py-0.5 rounded">Sc {n}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      <button onClick={() => handleDeleteCostume(c.id)}
                        className="opacity-0 group-hover:opacity-100 text-[#666] hover:text-red-400 transition-all shrink-0"
                        data-testid={`delete-costume-${c.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : !addingCostume && costumeContinuity.length === 0 && (
                <p className="text-xs text-[#444] italic">No costumes added yet.</p>
              )}
            </div>
          </div>
        </div>

        {lightbox && (
          <ImageLightbox images={lightbox.images} initialIndex={lightbox.idx} onClose={() => setLightbox(null)} />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function CastingDeptPage() {
  const { projectId } = useParams();
  const { token } = useAuth();
  const [characters, setCharacters] = useState([]);
  const [profiles, setProfiles] = useState({}); // { char_name: profile }
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedChar, setSelectedChar] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchData(); }, [projectId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [breakdownRes, profilesRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}/breakdown`, { headers }),
        axios.get(`${API}/projects/${projectId}/character-profiles`, { headers }),
      ]);

      const sceneData = breakdownRes.data;

      // Build char → scenes map from breakdown
      const charMap = {};
      sceneData.forEach(scene => {
        (scene.characters_in_scene || []).forEach(char => {
          if (!charMap[char]) charMap[char] = { name: char, scenes: [], totalPages: 0, isExtra: false };
          charMap[char].scenes.push(scene);
          charMap[char].totalPages += scene.page_count_eighths || 0;
        });
        (scene.breakdown_items?.EXTRA_BACKGROUND || []).forEach(item => {
          if (!charMap[item.description]) {
            charMap[item.description] = { name: item.description, scenes: [], totalPages: 0, isExtra: true };
          }
        });
      });

      setCharacters(Object.values(charMap).sort((a, b) => b.scenes.length - a.scenes.length));

      // Index profiles by name
      const profMap = {};
      (profilesRes.data || []).forEach(p => { profMap[p.name] = p; });
      setProfiles(profMap);
    } catch { toast.error('Failed to load casting data'); }
    finally { setLoading(false); }
  };

  const handleProfileUpdated = (charName, updated) => {
    setProfiles(prev => ({ ...prev, [charName]: updated }));
  };

  const filtered = search
    ? characters.filter(c => c.name.toLowerCase().includes(search.toLowerCase()))
    : characters;
  const principals = filtered.filter(c => !c.isExtra);
  const extras = filtered.filter(c => c.isExtra);

  const selectedCharData = selectedChar
    ? characters.find(c => c.name === selectedChar)
    : null;

  return (
    <div className="min-h-screen bg-[#0d0d0d] text-[#f0f0f0]">
      {/* Header */}
      <div className="border-b border-[#2a2a2a] px-6 py-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-['DM_Serif_Display'] text-2xl text-[#f0f0f0]">Casting</h1>
            <p className="text-sm text-[#a0a0a0] mt-0.5">
              Character profiles, scene appearances, costumes, and shoot days.
            </p>
          </div>
          <div className="flex items-center gap-6 text-sm">
            <div className="text-center">
              <div className="text-xl font-bold text-[#e8c547]" data-testid="principal-cast-count">{principals.length}</div>
              <div className="text-xs text-[#a0a0a0]">Principal Cast</div>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold text-[#f0f0f0]" data-testid="background-count">{extras.length}</div>
              <div className="text-xs text-[#a0a0a0]">Background</div>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#a0a0a0]">Loading...</div>
      ) : (
        <div className="p-6">
          {/* Search */}
          <div className="relative mb-6 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#a0a0a0]" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search characters..."
              className="pl-10 bg-[#1a1a1a] border-[#2a2a2a] text-[#f0f0f0]"
              data-testid="cast-search"
            />
          </div>

          {/* Principal Cast */}
          {principals.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xs font-semibold text-[#a0a0a0] uppercase tracking-widest mb-4">
                Principal Cast ({principals.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {principals.map(char => (
                  <CharacterCard
                    key={char.name}
                    char={char}
                    profile={profiles[char.name]}
                    token={token}
                    onClick={() => setSelectedChar(char.name)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Background */}
          {extras.length > 0 && (
            <div>
              <h2 className="text-xs font-semibold text-[#a0a0a0] uppercase tracking-widest mb-4">
                Background ({extras.length})
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {extras.map(char => (
                  <Card key={char.name} className="bg-[#1a1a1a] border-[#2a2a2a] p-3" data-testid={`extra-${char.name}`}>
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-[#a0a0a0] shrink-0" />
                      <div className="text-sm text-[#f0f0f0] truncate">{char.name}</div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {principals.length === 0 && extras.length === 0 && (
            <Card className="bg-[#1a1a1a] border-[#2a2a2a] p-12 text-center">
              <Users className="h-12 w-12 text-[#2a2a2a] mx-auto mb-3" />
              <p className="text-[#a0a0a0]">No characters found. Upload and parse a script to see cast.</p>
            </Card>
          )}
        </div>
      )}

      {/* Expanded Cast Modal */}
      {selectedChar && selectedCharData && (
        <CharacterModal
          char={selectedCharData}
          initialProfile={profiles[selectedChar]}
          token={token}
          projectId={projectId}
          onClose={() => setSelectedChar(null)}
          onProfileUpdated={handleProfileUpdated}
        />
      )}
    </div>
  );
}
